<script>
import { iSpinner, iTooltip } from '@inscribe/signal';
import SignalMetadataLine from '@/V2/documents/document-view/signals/SignalMetadataLine.vue';
import MissingValueTooltip from '@/V2/documents/document-view/signals/MissingValueTooltip.vue';
import * as SignalNames from '@/constants/signalNames';
import DocumentRightPanelEmptyState from '@/V2/documents/document-view/DocumentRightPanelEmptyState';
import format from '../../../../format';
import { formatFromNow } from '@/utils/date';
import moment from 'moment';
import { SOFTWARE, DATES, TEXT_LAYER_TEXT_GROUP } from '@/constants/metadata';
import * as metadataProperties from '@/constants/metadataProperties';
import { displayMetadataValue } from '@/utils/metadata';

export default {
  components: {
    DocumentRightPanelEmptyState,
    iSpinner,
    iTooltip,
    SignalMetadataLine,
    MissingValueTooltip,
  },
  props: {
    document: {
      type: Object,
      required: true,
    },
    signalGroup: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      SignalNames,
      moment,
      format,
      SOFTWARE,
      TEXT_LAYER_TEXT_GROUP,
      metadataProperties,
      DATES,
      displayMetadataValue,
    };
  },
  computed: {
    blockListResults() {
      const pages =
        this.document.fraud_results?.blacklists?.details?.pages ?? [];
      return this.generateBlocklistResultsTable(pages);
    },
    templatePages() {
      return this.document?.fraud_results?.templates?.details?.pages;
    },
    rejectedCustomerDetails() {
      const rejected_customer = this.document.fraud_results?.rejected_customer;
      return rejected_customer?.details?.rejected_details || [];
    },
    highRiskCustomerSeenBeforeDetails() {
      const high_risk_customer_seen_before =
        this.document.fraud_results?.high_risk_customer_seen_before;
      return high_risk_customer_seen_before?.details?.customers || [];
    },
    allDocumentMetadata() {
      const metadataObject = {};
      Object.keys(this.document.metadata).forEach((key) => {
        Object.keys(this.document.metadata[key]).forEach((subKey) => {
          let currentKey = subKey.toLowerCase();
          let currentValue = {
            type: key,
            value: this.document.metadata[key][subKey],
          };
          if (metadataObject[currentKey]) {
            metadataObject[currentKey].push(currentValue);
          } else {
            metadataObject[currentKey] = [currentValue];
          }
        });
      });
      return metadataObject;
    },
    softwareBlocklistDetails() {
      return Object.keys(this.document.fraud_results || []).filter(
        (signalKey) => signalKey === SignalNames.SOFTWARE_BLOCKLIST,
      );
    },
    blockListResultsWithTooltips() {
      return this.blockListResults.map((block, rowIndex) => ({
        ...block,
        nameTooltip: this.createBlocklistTooltipContent(
          'Name',
          block.blacklist?.name,
          `blk-${rowIndex}-name`,
        ),
        addressTooltip: this.createBlocklistTooltipContent(
          'Address',
          block.blacklist?.address?.normalized,
          `blk-${rowIndex}-address`,
        ),
        itemTooltip: this.createBlocklistTooltipContent(
          'Item',
          block.blocklist_item,
          `blk-${rowIndex}-item`,
        ),
      }));
    },
  },
  methods: {
    getMetadataValue(metadataArray) {
      if (!metadataArray?.length) return '';
      const allSameValue = metadataArray.every(
        (item) => item.value === metadataArray[0].value,
      );
      if (allSameValue) {
        return displayMetadataValue(metadataArray[0].value);
      }
      return metadataArray
        .map((item) => `${displayMetadataValue(item.value)} (${item.type})`)
        .join(', ');
    },
    generateBlocklistResultsTable(pages) {
      return pages.reduce(
        (pageAcc, page) => [
          ...pageAcc,
          ...page.instances.reduce(
            (instanceAcc, instance, instanceIndex) => [
              ...instanceAcc,
              {
                id: `${page.page_id}-${instanceIndex}`,
                page_number: page.page_number,
                ...instance,
                type: this.capitalizeFirstLetter(instance.type),
                blocklist_item:
                  instance.type === 'NAME'
                    ? instance.blacklist?.name
                    : instance.blacklist?.address?.normalized,
              },
            ],
            [],
          ),
        ],
        [],
      );
    },
    capitalizeFirstLetter(word) {
      const lower = word.toLowerCase();
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    },
    toSnakeCase(str) {
      return str
        .replace(/([A-Z])/g, '_$1')
        .toLowerCase()
        .replace(/^_/, '')
        .toUpperCase();
    },
    formatFromNow,
    createBlocklistTooltipContent(fieldName, value, blockId) {
      const isValueMissing =
        !value || value === null || value === undefined || value === '';
      return {
        value,
        tooltipId: `${fieldName.toLowerCase().replace(/\s+/g, '-')}-tooltip-${blockId}`,
        tooltipText: `${fieldName} is not available because the blocklist item is deleted`,
        showTooltip: isValueMissing,
      };
    },
  },
};
</script>

<template>
  <div class="databases-table-wrapper d-flex flex-grow-1 flex-wrap">
    <div
      v-if="document.fraud_results"
      class="data-container d-flex mx-auto flex-column"
    >
      <!-- BlockLists -->
      <div
        v-if="signalGroup.signalKey === SignalNames.BLOCKLISTS"
        class="d-flex flex-column i-gap-9"
      >
        <div
          v-for="(block, blockIndex) of blockListResultsWithTooltips"
          :key="`${block.id}-${blockIndex}`"
          class="data-table d-flex flex-column i-radius-xl i-px-9 i-py-4"
        >
          <!-- name -->
          <signal-metadata-line title="Name">
            <template #value>
              <missing-value-tooltip :tooltip-data="block.nameTooltip" />
            </template>
          </signal-metadata-line>

          <!-- address -->
          <signal-metadata-line title="Address">
            <template #value>
              <missing-value-tooltip :tooltip-data="block.addressTooltip" />
            </template>
          </signal-metadata-line>

          <!-- page number -->
          <signal-metadata-line title="Page Number">
            <template #value>
              <span>{{ block.page_number }}</span>
            </template>
          </signal-metadata-line>

          <!-- matched text -->
          <signal-metadata-line title="Matched text">
            <template #value>
              <span>{{ block.matched_text }}</span>
            </template>
          </signal-metadata-line>

          <!-- type -->
          <signal-metadata-line title="Type">
            <template #value>
              <span>{{ block.type }}</span>
            </template>
          </signal-metadata-line>

          <!-- item -->
          <signal-metadata-line title="Item">
            <template #value>
              <missing-value-tooltip :tooltip-data="block.itemTooltip" />
            </template>
          </signal-metadata-line>
        </div>
      </div>

      <!-- rejected customers -->
      <div
        v-if="signalGroup.signalKey === SignalNames.REJECTED_CUSTOMER"
        class="d-flex flex-column i-gap-9"
      >
        <div
          v-for="(rejection, rejectionIndex) of rejectedCustomerDetails"
          :key="rejectionIndex"
          class="data-table d-flex flex-column i-radius-xl i-px-9 i-py-4"
        >
          <!-- customer -->
          <signal-metadata-line title="Customer">
            <template #value>
              <router-link
                v-if="rejection.customer?.id"
                :to="`/customer/${rejection.customer?.id}`"
              >
                <span>{{ rejection.customer?.name }}</span>
              </router-link>
            </template>
          </signal-metadata-line>

          <!-- address -->
          <signal-metadata-line title="Address">
            <template #value>
              <span>{{ rejection.address }}</span>
            </template>
          </signal-metadata-line>
        </div>
      </div>

      <!-- high risk customers seen before -->
      <div
        v-if="
          signalGroup.signalKey === SignalNames.HIGH_RISK_CUSTOMER_SEEN_BEFORE
        "
        class="d-flex flex-column i-gap-9"
      >
        <div
          v-for="(
            customerSeenBefore, customerSeenBeforeIndex
          ) of highRiskCustomerSeenBeforeDetails"
          :key="customerSeenBeforeIndex"
          class="data-table d-flex flex-column i-radius-xl i-px-9 i-py-4"
        >
          <!-- customer link -->
          <signal-metadata-line title="Customer">
            <template #value>
              <router-link
                v-if="customerSeenBefore.id"
                :to="`/customer/${customerSeenBefore.id}`"
              >
                <span>{{ customerSeenBefore.name }}</span>
              </router-link>
            </template>
          </signal-metadata-line>

          <!-- date -->
          <signal-metadata-line title="Date">
            <template #value>
              <div v-if="customerSeenBefore.id">
                <span :id="'customer-' + customerSeenBefore.id">{{
                  formatFromNow(customerSeenBefore.created_at)
                }}</span>
                <i-tooltip
                  :target="`#customer-${customerSeenBefore.id}`"
                  :title="
                    moment(customerSeenBefore.created_at).format(
                      format.time_format,
                    )
                  "
                />
              </div>
            </template>
          </signal-metadata-line>

          <!-- owner -->
          <signal-metadata-line title="Owner">
            <template #value>
              <span>{{ customerSeenBefore.creator.email }}</span>
            </template>
          </signal-metadata-line>
        </div>
      </div>

      <!-- templates -->
      <div
        v-if="signalGroup.signalKey === SignalNames.TEMPLATES"
        class="d-flex flex-column i-gap-9"
      >
        <document-right-panel-empty-state
          v-if="templatePages.length === 0"
          symbol="medium-file"
          title="No templates matched"
          sub-title="The document doesn't match any templates on file"
        ></document-right-panel-empty-state>

        <div
          v-for="template of templatePages"
          :key="template.id"
          class="data-table d-flex flex-column i-radius-xl i-px-9 i-py-4"
        >
          <!-- Similarity -->
          <signal-metadata-line title="Similarity">
            <template #value>
              <span
                >{{
                  parseFloat((template.similarity * 100)?.toFixed(2))
                }}%</span
              >
            </template>
          </signal-metadata-line>

          <!-- Page number -->
          <signal-metadata-line title="Page number">
            <template #value>
              <span>{{ template.page_number }}</span>
            </template>
          </signal-metadata-line>
        </div>
      </div>

      <!-- software -->
      <div
        v-if="signalGroup.signalKey === SignalNames.SOFTWARE_BLOCKLIST"
        class="d-flex flex-column i-gap-2"
      >
        <template v-for="metadataKey in SOFTWARE" :key="metadataKey">
          <signal-metadata-line
            v-if="allDocumentMetadata[metadataKey.toLowerCase()]?.length > 0"
            :title="metadataProperties[toSnakeCase(metadataKey)] || metadataKey"
          >
            <template #value>
              {{
                getMetadataValue(allDocumentMetadata[metadataKey.toLowerCase()])
              }}
            </template>
          </signal-metadata-line>
        </template>
      </div>

      <!-- dates mismatch -->
      <div
        v-if="signalGroup.signalKey === SignalNames.DATE_METADATA"
        class="d-flex flex-column i-gap-2"
      >
        <template v-for="metadataKey in DATES" :key="metadataKey">
          <signal-metadata-line
            v-if="allDocumentMetadata[metadataKey.toLowerCase()]?.length > 0"
            :title="metadataProperties[toSnakeCase(metadataKey)] || metadataKey"
          >
            <template #value>
              {{
                getMetadataValue(allDocumentMetadata[metadataKey.toLowerCase()])
              }}
            </template>
          </signal-metadata-line>
        </template>
      </div>

      <!-- text layer text -->
      <div
        v-if="signalGroup.signalKey === SignalNames.TEXT_LAYER_TEXT"
        class="d-flex flex-column i-gap-2"
      >
        <template
          v-for="metadataKey in TEXT_LAYER_TEXT_GROUP"
          :key="metadataKey"
        >
          <signal-metadata-line
            v-if="allDocumentMetadata[metadataKey.toLowerCase()]?.length > 0"
            title="Added Text"
          >
            <template #value>
              {{
                getMetadataValue(allDocumentMetadata[metadataKey.toLowerCase()])
              }}
            </template>
          </signal-metadata-line>
        </template>
      </div>
    </div>
    <div v-else class="d-flex w-100 align-items-center justify-content-center">
      <i-spinner></i-spinner>
    </div>
  </div>
</template>

<style lang="scss">
.databases-table-wrapper {
  overflow: auto;
  max-height: 100%;
}
</style>
